import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import AnalyticsCharts from "./AnalyticsCharts";
import AnalyticsGrowthCharts from "./AnalyticsGrowthCharts";
import AnalyticsAssetPoolCharts from "./AnalyticsAssetPoolCharts";
import AnalyticsRevenueCharts from "./AnalyticsRevenueCharts";
import { ENDPOINT_BASE_URL } from "../../configs/config";
import {
  fetchMonitorAprAttributes,
  fetchPortfolioLatestData,
} from "../../dashboard/components/requests/portfolio-requests";
import { fetchDuneQuery } from "./requests/revenue-requests";
import { fetchMarketData } from "./requests/market-requests";
import styles from "./analytics.module.css";

const normalizeDateString = (timestamp) => {
  if (!timestamp) return "";
  const ts = String(timestamp).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(ts)) {
    return ts.slice(0, 10);
  }
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toUtcDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
  );
};

const transformRecords = (records) =>
  records.map((record) => {
    const dateStr = normalizeDateString(record.timestamp);

    // Convert decimal values - keep as decimals, will multiply by 100 when displaying
    // Handle null/undefined/empty string values
    let ninetyDayApy = null;
    let thirtyDayApy = null;

    // Check for ninety_day_apy - handle various formats
    const rawNinety = record.ninety_day_apy;
    if (
      rawNinety != null &&
      rawNinety !== "" &&
      rawNinety !== "null" &&
      rawNinety !== "undefined"
    ) {
      const num = Number(rawNinety);
      if (!Number.isNaN(num)) {
        ninetyDayApy = num;
      }
    }

    // Check for thirty_day_apy - handle various formats
    const rawThirty = record.thirty_day_apy;
    if (
      rawThirty != null &&
      rawThirty !== "" &&
      rawThirty !== "null" &&
      rawThirty !== "undefined"
    ) {
      const num = Number(rawThirty);
      if (!Number.isNaN(num)) {
        thirtyDayApy = num;
      }
    }

  return {
      date: dateStr,
      expectedApr:
        record.portfolio_expected_apr != null
          ? Number(record.portfolio_expected_apr)
          : 0,
      impliedApr:
        record.portfolio_implied_apr != null
          ? Number(record.portfolio_implied_apr)
          : 0,
      apy90Day: ninetyDayApy,
      apy30Day: thirtyDayApy,
      dollarValueGrowth:
        record.dollar_value_growth != null ? Number(record.dollar_value_growth) : null,
      totalAssetsValue:
        record.total_assets_value != null ? Number(record.total_assets_value) : null,
      usdiInCirculation:
        record.usdi_in_circulation != null ? Number(record.usdi_in_circulation) : null,
    };
  });

const GROWTH_INCEPTION_DATE = "2024-06-28";

export default function Analytics() {
  const [selectedCategory, setSelectedCategory] = useState("APR Metrics");
  const [isMobile, setIsMobile] = useState(false);
  const [timeRange, setTimeRange] = useState("ALL");
  const [data, setData] = useState([]);
  const [assetPool, setAssetPool] = useState({
    current: [],
    simulated: [],
    monitorAll: [],
    totalValue: 0,
    loading: true,
  });
  const [marketData, setMarketData] = useState({ loading: true, map: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revenueRange, setRevenueRange] = useState("Monthly");
  const [revenueData, setRevenueData] = useState({
    loading: true,
    rows: [],
    revenueSeries: [],
    commissionSeries: [],
    monthlyBreakdown: [],
    totals: {
      netProfit: 0,
      grossProfit: 0,
      commission: 0,
      rate: null,
      peakNetProfit: 0,
    },
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
          `${ENDPOINT_BASE_URL}/track_records_daily_latest`
        );
        
        if (!response.ok) {
          throw new Error("Failed to fetch analytics data");
        }
        
        const apiData = await response.json();
        const records = Array.isArray(apiData?.data) ? apiData.data : [];
        console.log("Analytics API response preview:", {
          totalRecords: records.length,
          sampleFirst: records[0],
          sampleLast: records[records.length - 1],
        });
        const transformedData = transformRecords(records);
        
        // Sort by date (oldest first) - ensures left->right oldest to newest
        const sortedData = transformedData.sort((a, b) => {
          const dateA = toUtcDate(a.date);
          const dateB = toUtcDate(b.date);
          if (!dateA || !dateB) return 0;
          return dateA.getTime() - dateB.getTime();
        });
        
        // Log first few records to verify sorting
        if (sortedData.length > 0) {
          console.log("First record (oldest):", {
            date: sortedData[0].date,
            apy30Day: sortedData[0].apy30Day,
            apy90Day: sortedData[0].apy90Day
          });
        }

        if (sortedData.length > 0) {
          const growthValues = sortedData
            .map((item) => Number(item.dollarValueGrowth))
            .filter((value) => Number.isFinite(value));
          console.log("Growth metrics raw data:", {
            records: sortedData.length,
            oldestDate: sortedData[0]?.date,
            latestDate: sortedData[sortedData.length - 1]?.date,
            firstFive: sortedData
              .slice(0, 5)
              .map((item) => ({
                date: item.date,
                dollarValueGrowth: item.dollarValueGrowth,
                totalAssetsValue: item.totalAssetsValue,
                usdiInCirculation: item.usdiInCirculation,
              })),
            lastFive: sortedData
              .slice(-5)
              .map((item) => ({
                date: item.date,
                dollarValueGrowth: item.dollarValueGrowth,
                totalAssetsValue: item.totalAssetsValue,
                usdiInCirculation: item.usdiInCirculation,
              })),
            growthMin: growthValues.length ? Math.min(...growthValues) : null,
            growthMax: growthValues.length ? Math.max(...growthValues) : null,
          });
        }
        
        setData(sortedData);
      } catch (err) {
        console.error("Error fetching analytics data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  useEffect(() => {
    const loadRevenue = async () => {
      try {
        setRevenueData((prev) => ({ ...prev, loading: true }));

        const [revenueRows, commissionRows] = await Promise.all([
          fetchDuneQuery(4927203),
          fetchDuneQuery(4901005),
        ]);

        console.log("Revenue Dune rows (sample):", {
          revenueCount: revenueRows.length,
          revenueSample: revenueRows.slice(0, 5),
          commissionCount: commissionRows.length,
          commissionSample: commissionRows.slice(0, 5),
        });

        const parseDateKey = (row) => {
          const keys = ["month", "date", "day", "dt", "timestamp"];
          for (const key of keys) {
            if (row[key]) return row[key];
          }
          const entry = Object.entries(row).find(([k]) =>
            k.toLowerCase().includes("date")
          );
          return entry ? entry[1] : null;
        };

        const parseValue = (row, candidates) => {
          for (const key of candidates) {
            if (row[key] != null) return Number(row[key]);
          }
          const entry = Object.entries(row).find(([k]) =>
            candidates.some((candidate) => k.toLowerCase().includes(candidate))
          );
          return entry ? Number(entry[1]) : null;
        };

        const toMonthKey = (dateValue) => {
          const parsed = new Date(dateValue);
          if (Number.isNaN(parsed.getTime())) {
            return null;
          }
          const year = parsed.getUTCFullYear();
          const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
          return `${year}-${month}-01`;
        };

        const aggregateRows = (rows, valueKeys) => {
          const map = new Map();
          rows.forEach((row) => {
            const dateValue = parseDateKey(row);
            const monthKey = toMonthKey(dateValue);
            if (!monthKey) return;
            const value = parseValue(row, valueKeys);
            if (!Number.isFinite(value)) return;
            map.set(monthKey, (map.get(monthKey) || 0) + value);
          });
          return map;
        };

        const revenueMap = aggregateRows(revenueRows, [
          "amount",
          "revenue",
          "total_revenue",
          "gross_revenue",
          "recurring_revenue",
        ]);
        const commissionMap = aggregateRows(commissionRows, [
          "amount",
          "commission",
          "management_fee",
          "fees",
          "fee",
        ]);

        const months = Array.from(
          new Set([...revenueMap.keys(), ...commissionMap.keys()])
        ).sort();

        const revenueSeries = months.map((month) => ({
          month,
          value: revenueMap.get(month) || 0,
        }));
        const commissionSeries = months.map((month) => ({
          month,
          value: commissionMap.get(month) || 0,
        }));

        let cumulativeNetProfit = 0;
        let cumulativeCommission = 0;
        let cumulativeGrossProfit = 0;
        const monthlyBreakdown = months.map((month) => {
          const netProfitValue = revenueMap.get(month) || 0;
          const commissionValue = commissionMap.get(month) || 0;
          const grossProfitValue = netProfitValue + commissionValue;
          cumulativeNetProfit += netProfitValue;
          cumulativeCommission += commissionValue;
          cumulativeGrossProfit += grossProfitValue;
          return {
            month,
            netProfitValue,
            grossProfitValue,
            commissionValue,
            cumulativeNetProfit,
            cumulativeGrossProfit,
            cumulativeCommission,
          };
        });

        const totalNetProfit = revenueSeries.reduce(
          (sum, item) => sum + item.value,
          0
        );
        const totalCommission = commissionSeries.reduce(
          (sum, item) => sum + item.value,
          0
        );
        const totalGrossProfit = totalNetProfit + totalCommission;
        const peakNetProfit = revenueSeries.reduce(
          (max, item) => Math.max(max, item.value),
          0
        );

        setRevenueData({
          loading: false,
          rows: months,
          revenueSeries,
          commissionSeries,
          monthlyBreakdown,
          totals: {
            netProfit: totalNetProfit,
            grossProfit: totalGrossProfit,
            commission: totalCommission,
            rate:
              totalGrossProfit > 0
                ? (totalCommission / totalGrossProfit) * 100
                : null,
            peakNetProfit,
          },
        });
      } catch (err) {
        console.error("Error fetching revenue data:", err);
        setRevenueData((prev) => ({ ...prev, loading: false }));
      }
    };

    loadRevenue();
  }, []);

  useEffect(() => {
    const loadAssetPool = async () => {
      try {
        setAssetPool((prev) => ({ ...prev, loading: true }));
        const [currentRaw, simulatedRaw] = await Promise.all([
          fetchPortfolioLatestData(),
          fetchMonitorAprAttributes(),
        ]);

        const currentFiltered = combineCurrentPool(
          (Array.isArray(currentRaw) ? currentRaw : []).filter(
            (item) => Number(item.amount) > 0
          )
        );

        const totalValue = currentFiltered.reduce(
          (sum, item) => sum + (Number(item.assets_value) || 0),
          0
        );

        const simulatedAll = Array.isArray(simulatedRaw) ? simulatedRaw : [];
        const simulatedFiltered = simulatedAll.filter(
          (item) => {
            const target =
              Number(item.allocation_percentage) ||
              Number(item.target_allocation_percent) ||
              Number(item.portfolio_allocation) * 100 ||
              0;
            return target > 0;
          }
        );

        setAssetPool({
          current: currentFiltered,
          simulated: simulatedFiltered,
          monitorAll: simulatedAll,
          totalValue,
          loading: false,
        });

        const debugMap = buildMonitorAprMap(simulatedFiltered);
        console.log("Asset pool APR mapping (debug):", {
          simulatedSample: simulatedFiltered.slice(0, 5).map((item) => ({
            symbol: item.symbol,
            utc_timestamp: item.utc_timestamp,
            apr_score: item.apr_score,
          })),
          simulatedCount: simulatedFiltered.length,
          currentCount: currentFiltered.length,
          samples: currentFiltered.map((item) => {
            const monitorSymbol = resolveMonitorSymbol(item.symbol);
            const monitorRow = debugMap.get(monitorSymbol);
            return {
              symbol: item.symbol,
              displaySymbol: resolveDisplaySymbol(item.symbol),
              monitorSymbol,
              apr_score: monitorRow?.apr_score ?? null,
            };
          }),
        });
      } catch (err) {
        console.error("Error fetching asset pool data:", err);
        setAssetPool((prev) => ({ ...prev, loading: false }));
      }
    };

    loadAssetPool();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadMarketData = async () => {
      const symbols = new Set();
      (Array.isArray(assetPool.monitorAll) ? assetPool.monitorAll : []).forEach(
        (item) => {
          const label = resolveDisplaySymbol(item.symbol);
          if (label && label !== "—") {
            symbols.add(label.toUpperCase());
          }
        }
      );

      if (!symbols.size) {
        setMarketData({ loading: false, map: {} });
        return;
      }

      try {
        setMarketData((prev) => ({ ...prev, loading: true }));
        const payload = await fetchMarketData(Array.from(symbols));
        console.log("Market data (debug):", {
          symbols: Array.from(symbols),
          missing: payload?.missing || [],
          sample: payload?.data ? Object.entries(payload.data) : [],
        });
        if (!cancelled) {
          setMarketData({
            loading: false,
            map: payload?.data || {},
          });
        }
      } catch (err) {
        console.error("Error fetching market data:", err);
        if (!cancelled) {
          setMarketData({ loading: false, map: {} });
        }
      }
    };

    loadMarketData();

    return () => {
      cancelled = true;
    };
  }, [assetPool.monitorAll]);

  const categories = [
    "APR Metrics",
    "Growth Metrics",
    "Asset Pool Metrics",
    "Revenue & Commission",
  ];

  const timeRanges = ["1M", "3M", "6M", "1Y", "ALL"];

  const currentValues = useMemo(() => {
    if (data.length === 0) {
      return {
        expectedApr: 0,
        impliedApr: 0,
        apy90Day: null,
        apy30Day: null,
      };
    }
    
    // Data is sorted oldest -> newest, so use the last record
    // If the latest record has null, show null (don't show older values)
    const latest = data[data.length - 1];
    
    return {
      expectedApr: latest.expectedApr,
      impliedApr: latest.impliedApr,
      apy90Day: latest.apy90Day ?? null,
      apy30Day: latest.apy30Day ?? null,
    };
  }, [data]);

  const growthSeries = useMemo(() => {
    if (!data.length) {
      return { labels: [], values: [] };
    }
    const sortedAsc = [...data].sort((a, b) => {
      const dateA = toUtcDate(a.date);
      const dateB = toUtcDate(b.date);
      if (!dateA || !dateB) return 0;
      return dateA.getTime() - dateB.getTime();
    });
    const labels = sortedAsc.map((item) => item.date);
    const rawValues = sortedAsc.map((item) => item.dollarValueGrowth);
    // API reports ~0.001 on inception; baseline should be 1. Only fix that point.
    const values = [...rawValues];
    const inceptionIndex = labels.indexOf(GROWTH_INCEPTION_DATE);
    if (inceptionIndex !== -1) {
      values[inceptionIndex] = 1;
    } else if (values.length > 0 && Number(values[0]) < 0.5) {
      values[0] = 1;
    }
    return { labels, values };
  }, [data]);

  const assetsSeries = useMemo(() => {
    if (!data.length) {
      return { labels: [], totalAssets: [], usdi: [] };
    }
    const sortedAsc = [...data].sort((a, b) => {
      const dateA = toUtcDate(a.date);
      const dateB = toUtcDate(b.date);
      if (!dateA || !dateB) return 0;
      return dateA.getTime() - dateB.getTime();
    });
    return {
      labels: sortedAsc.map((item) => item.date),
      totalAssets: sortedAsc.map((item) => item.totalAssetsValue),
      usdi: sortedAsc.map((item) => item.usdiInCirculation),
    };
  }, [data]);

  const monthlyGrowth = useMemo(() => {
    if (!growthSeries.labels.length) {
      return { labels: [], values: [] };
    }

    const monthMap = new Map();
    for (let i = 0; i < growthSeries.labels.length; i++) {
      const label = growthSeries.labels[i];
      const value = Number(growthSeries.values[i]);
      if (!Number.isFinite(value)) {
        continue;
      }
      const date = toUtcDate(label);
      if (!date) {
        continue;
      }
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth() + 1;
      const key = `${year}-${String(month).padStart(2, "0")}`;
      const entry = monthMap.get(key);
      if (!entry) {
        monthMap.set(key, {
          year,
          month,
          startValue: value,
          endValue: value,
          startDate: date,
          endDate: date,
        });
        continue;
      }
      if (date < entry.startDate) {
        entry.startDate = date;
        entry.startValue = value;
      }
      if (date > entry.endDate) {
        entry.endDate = date;
        entry.endValue = value;
      }
    }

    const sortedMonths = Array.from(monthMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    const labels = [];
    const values = [];

    sortedMonths.forEach((entry) => {
      if (!Number.isFinite(entry.startValue) || !Number.isFinite(entry.endValue)) {
        return;
      }
      const monthlyReturn = (entry.endValue / entry.startValue - 1) * 100;
      const labelDate = new Date(Date.UTC(entry.year, entry.month - 1, 1));
      labels.push(
        labelDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })
      );
      values.push(monthlyReturn);
    });

    const sliceStart = Math.max(labels.length - 12, 0);
    return {
      labels: labels.slice(sliceStart),
      values: values.slice(sliceStart),
    };
  }, [growthSeries]);

  const growthSummary = useMemo(() => {
    const baselineDate = new Date(Date.UTC(2024, 6, 23)); // 2024-07-23
    const series = growthSeries.labels
      .map((label, index) => {
        const date = toUtcDate(label);
        const value = Number(growthSeries.values[index]);
        if (!date || !Number.isFinite(value) || value <= 0) {
          return null;
        }
        return { date, value };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);

    const baselineSeries = series.filter((item) => item.date >= baselineDate);
    const usable = baselineSeries.length ? baselineSeries : series;

    if (usable.length < 2) {
      return { total: null, byYear: new Map() };
    }

    // dollar_value_growth is already normalized to 1 at baseline
    const total = (usable[usable.length - 1].value - 1) * 100;

    const yearly = new Map();
    usable.forEach((item) => {
      const year = item.date.getUTCFullYear();
      if (!yearly.has(year)) {
        yearly.set(year, { start: item.value, end: item.value });
      } else {
        yearly.get(year).end = item.value;
      }
    });

    return { total, byYear: yearly };
  }, [growthSeries]);

  const growthMonthlyBreakdown = useMemo(() => {
    if (!growthSeries.labels.length) {
      return [];
    }

    const monthMap = new Map();
    for (let i = 0; i < growthSeries.labels.length; i++) {
      const date = toUtcDate(growthSeries.labels[i]);
      const value = Number(growthSeries.values[i]);
      if (!date || !Number.isFinite(value)) {
        continue;
      }
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const key = `${year}-${month}`;
      const entry = monthMap.get(key);
      if (!entry) {
        monthMap.set(key, {
          year,
          month,
          startValue: value,
          endValue: value,
          startDate: date,
          endDate: date,
        });
        continue;
      }
      if (date < entry.startDate) {
        entry.startDate = date;
        entry.startValue = value;
      }
      if (date > entry.endDate) {
        entry.endDate = date;
        entry.endValue = value;
      }
    }

    let ascending = Array.from(monthMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    if (ascending.length > 0) {
      const last = ascending[ascending.length - 1];
      const now = new Date();
      const currentYear = now.getUTCFullYear();
      const currentMonth = now.getUTCMonth();
      if (last.year === currentYear && last.month === currentMonth) {
        const endDate = last.endDate;
        const endOfMonth = new Date(
          Date.UTC(last.year, last.month + 1, 0, 0, 0, 0)
        );
        if (endDate && endDate < endOfMonth) {
          ascending = ascending.slice(0, -1);
        }
      }
    }

    const withChanges = ascending.map((entry, index) => {
      const prev = ascending[index - 1];
      const monthlyChange =
        prev && Number.isFinite(prev.endValue)
          ? entry.endValue - prev.endValue
          : null;
      const monthlyChangePct =
        prev && Number.isFinite(prev.endValue) && prev.endValue > 0
          ? ((entry.endValue / prev.endValue) - 1) * 100
          : null;
      const labelDate = new Date(Date.UTC(entry.year, entry.month + 1, 1));
      return {
        label: labelDate.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
        cumulative: entry.endValue,
        monthlyChange,
        monthlyChangePct,
      };
    });

    return withChanges.slice().reverse();
  }, [growthSeries]);

  const assetsMonthlyBreakdown = useMemo(() => {
    if (!assetsSeries.labels.length) {
      return [];
    }

    const monthMap = new Map();
    for (let i = 0; i < assetsSeries.labels.length; i++) {
      const date = toUtcDate(assetsSeries.labels[i]);
      const totalAssets = Number(assetsSeries.totalAssets[i]);
      const usdi = Number(assetsSeries.usdi[i]);
      if (!date || !Number.isFinite(totalAssets) || !Number.isFinite(usdi)) {
        continue;
      }
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const key = `${year}-${month}`;
      const entry = monthMap.get(key);
      if (!entry || date > entry.date) {
        monthMap.set(key, {
          year,
          month,
          date,
          totalAssets,
          usdi,
        });
      }
    }

    let monthRows = Array.from(monthMap.values())
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      })
      .map((entry) => {
        const labelDate = new Date(Date.UTC(entry.year, entry.month + 1, 1));
        const difference = entry.totalAssets - entry.usdi;
        return {
          label: labelDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          }),
          totalAssets: entry.totalAssets,
          usdi: entry.usdi,
          difference,
          year: entry.year,
          month: entry.month,
          date: entry.date,
        };
      });

    if (monthRows.length > 0) {
      const latest = monthRows[0];
      if (latest?.date) {
        const now = new Date();
        const currentYear = now.getUTCFullYear();
        const currentMonth = now.getUTCMonth();
        if (latest.year === currentYear && latest.month === currentMonth) {
          const endOfMonth = new Date(
            Date.UTC(latest.year, latest.month + 1, 0, 0, 0, 0)
          );
          if (latest.date < endOfMonth) {
            monthRows = monthRows.slice(1);
          }
        }
      }
    }

    return monthRows.map(({ year, month, date, ...rest }) => rest);
  }, [assetsSeries]);

  const yearlyGrowthCards = useMemo(() => {
    const now = new Date();
    const targetYears = [now.getFullYear() - 2, now.getFullYear() - 1];
    return targetYears.map((year) => {
      const entry = growthSummary.byYear.get(year);
      if (!entry || !Number.isFinite(entry.start) || !Number.isFinite(entry.end)) {
        return { year, value: null };
      }
      const value = ((entry.end / entry.start) - 1) * 100;
      return { year, value };
    });
  }, [growthSummary]);

  const filteredData = useMemo(() => {
    const hardMinDate = toUtcDate("2024-10-22");
    if (timeRange === "ALL") {
      return hardMinDate
        ? data.filter((item) => {
            const itemDate = toUtcDate(item.date);
            return itemDate && itemDate >= hardMinDate;
          })
        : data;
    }

    if (data.length === 0) {
      return [];
    }

    // Use current UTC date as reference point
    const now = new Date();
    const nowUtc = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );
    let minDate = new Date(0); // earliest date for "ALL" (but we already returned above)
    
    // Calculate minimum date based on time range
    switch (timeRange) {
      case "1M":
        minDate = new Date(nowUtc);
        minDate.setUTCMonth(nowUtc.getUTCMonth() - 1);
        break;
      case "3M":
        minDate = new Date(nowUtc);
        minDate.setUTCMonth(nowUtc.getUTCMonth() - 3);
        break;
      case "6M":
        minDate = new Date(nowUtc);
        minDate.setUTCMonth(nowUtc.getUTCMonth() - 6);
        break;
      case "1Y":
        minDate = new Date(nowUtc);
        minDate.setUTCFullYear(nowUtc.getUTCFullYear() - 1);
        break;
      default:
        return data;
    }

    const effectiveMinDate =
      hardMinDate && hardMinDate > minDate ? hardMinDate : minDate;

    const filtered = data.filter((item) => {
      if (!item.date) return false;
      
      const itemDate = toUtcDate(item.date);
      if (!itemDate) {
        return false;
      }
      
      // Check if date is >= effectiveMinDate (filter out dates older than the range)
      return itemDate >= effectiveMinDate;
    });

    // Debug logging
    console.log(`Filtering for ${timeRange}:`, {
      totalRecords: data.length,
      filteredRecords: filtered.length,
      minDate: effectiveMinDate.toISOString().split("T")[0],
      now: nowUtc.toISOString().split("T")[0],
      latestDataDate: data[0]?.date,
      firstFilteredDate: filtered[0]?.date,
      lastFilteredDate: filtered[filtered.length - 1]?.date
    });

    return filtered;
  }, [data, timeRange]);

  const chartData = useMemo(() => {
    if (filteredData.length) {
      console.log("APR chart labels preview:", {
        first: filteredData[0]?.date,
        last: filteredData[filteredData.length - 1]?.date,
        count: filteredData.length,
      });
    }
    const sortedForChart = [...filteredData].sort((a, b) => {
      const dateA = toUtcDate(a.date);
      const dateB = toUtcDate(b.date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA - dateB; // oldest first
    });

    return {
      labels: sortedForChart.map((item) => item.date),
      expectedApr: sortedForChart.map((item) => item.expectedApr * 100),
      impliedApr: sortedForChart.map((item) => item.impliedApr * 100),
      // Convert to percentage and handle null values (set to null so chart.js skips them)
      apy90Day: sortedForChart.map((item) =>
        item.apy90Day != null && item.apy90Day !== 0 ? item.apy90Day * 100 : null
      ),
      apy30Day: sortedForChart.map((item) =>
        item.apy30Day != null && item.apy30Day !== 0 ? item.apy30Day * 100 : null
      ),
    };
  }, [filteredData]);

  const historicalRows = useMemo(() => {
    return [...filteredData].reverse();
  }, [filteredData]);

  const formatPercent = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    const sign = numeric >= 0 ? "" : "-";
    return `${sign}${Math.abs(numeric).toFixed(2)}%`;
  };

  const formatGrowthMultiple = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    return `${numeric.toFixed(4)}x`;
  };

  const formatCurrency = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    return numeric.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    });
  };

  const formatCurrencyOrDash = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    if (numeric === 0) {
      return "-";
    }
    return formatCurrency(numeric);
  };

  const formatCurrencyWhole = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    return numeric.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  };

  const formatCurrencyShort = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    if (numeric >= 1e9) {
      return `$${(numeric / 1e9).toFixed(1)}B`;
    }
    if (numeric >= 1e6) {
      return `$${(numeric / 1e6).toFixed(1)}M`;
    }
    if (numeric >= 1e3) {
      return `$${(numeric / 1e3).toFixed(1)}K`;
    }
    return `$${numeric.toFixed(0)}`;
  };

  const formatCompactCurrency = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    if (numeric === 0) {
      return "-";
    }
    if (numeric >= 1e9) {
      return `$${(numeric / 1e9).toFixed(1)}B`;
    }
    if (numeric >= 1e6) {
      return `$${Math.round(numeric / 1e6)}M`;
    }
    if (numeric >= 1e3) {
      return `$${Math.round(numeric / 1e3)}K`;
    }
    return `$${Math.round(numeric).toLocaleString("en-US")}`;
  };

  const formatRevenueMonthLabel = (monthKey, format = "long") => {
    const parsed = new Date(monthKey);
    if (Number.isNaN(parsed.getTime())) {
      return monthKey;
    }
    parsed.setUTCMonth(parsed.getUTCMonth() + 1);
    return parsed.toLocaleDateString("en-US", { month: format, year: "numeric" });
  };

  const revenueDateRangeLabel = (() => {
    const breakdown = revenueData.monthlyBreakdown || [];
    if (breakdown.length === 0) return null;
    const monthKeys = breakdown.map((r) => r.month).filter(Boolean);
    if (monthKeys.length === 0) return null;
    const dates = monthKeys.map((k) => new Date(k)).filter((d) => !Number.isNaN(d.getTime()));
    if (dates.length === 0) return null;
    const firstDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const lastDate = new Date(Math.max(...dates.map((d) => d.getTime())));
    const applyDisplayShift = (d) => {
      const c = new Date(d.getTime());
      c.setUTCMonth(c.getUTCMonth() + 1);
      return c;
    };
    const firstDisplay = applyDisplayShift(firstDate);
    const lastDisplay = applyDisplayShift(lastDate);
    const sameYear = firstDisplay.getUTCFullYear() === lastDisplay.getUTCFullYear();
    if (sameYear) {
      return `${firstDisplay.toLocaleDateString("en-US", { month: "long" })} - ${lastDisplay.toLocaleDateString("en-US", { month: "long" })} ${lastDisplay.getUTCFullYear()}`;
    }
    return `${firstDisplay.toLocaleDateString("en-US", { month: "long", year: "numeric" })} - ${lastDisplay.toLocaleDateString("en-US", { month: "long", year: "numeric" })}`;
  })();

  const shiftRevenueMonthKey = (monthKey) => {
    const parsed = new Date(monthKey);
    if (Number.isNaN(parsed.getTime())) {
      return monthKey;
    }
    parsed.setUTCMonth(parsed.getUTCMonth() + 1);
    return parsed.toISOString().slice(0, 10);
  };

  const formatBasisPercent = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    return `${numeric.toFixed(2)}%`;
  };

  const formatPercentFromDecimal = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    return formatPercent(numeric * 100);
  };

  const getValueClassFromDecimal = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "";
    }
    return getValueClass(numeric * 100);
  };

  const formatAprLabel = (symbol, aprValue) => {
    const upper = String(symbol || "").toUpperCase();
    if (upper === "USDT" || upper === "USDC") {
      return "Stablecoin";
    }
    const numeric = typeof aprValue === "number" ? aprValue : parseFloat(aprValue);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    return `${numeric.toFixed(2)}% APR`;
  };

  const buildMonitorAprMap = (items) => {
    const map = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!item?.symbol) return;
      map.set(String(item.symbol).toUpperCase(), item);
    });
    return map;
  };

  const resolveMonitorSymbol = (symbol) => {
    if (!symbol) return "";
    const upper = String(symbol).toUpperCase();
    if (upper.endsWith("USD_PERP")) {
      return upper;
    }
    if (upper === "BNSOL") {
      return "SOLUSD_PERP";
    }
    if (upper === "DOGE") {
      return "DOGEUSD_PERP";
    }
    return `${upper}USD_PERP`;
  };

  const resolveDisplaySymbol = (symbol) => {
    if (!symbol) return "—";
    const upper = String(symbol).toUpperCase();
    if (upper === "BNSOL") {
      return "SOL";
    }
    if (upper.endsWith("USD_PERP")) {
      return upper.replace(/_?USD_PERP$/, "");
    }
    return upper;
  };

  const monitorAprMap = useMemo(
    () => buildMonitorAprMap(assetPool.monitorAll),
    [assetPool.monitorAll]
  );

  const ASSET_PALETTE = [
    "#0f1a3d",
    "#1a2b5f",
    "#2c4a8c",
    "#5BA4E6",
    "#7a9bc7",
    "#2d8a5f",
    "#6aa0d6",
    "#355f88",
  ];

  const SIMULATED_COLORS = [
    "#5BA4E6B2", // 70%
    "#5BA4E68C", // 55%
    "#5BA4E666", // 40%
    "#5BA4E64D", // 30%
    "#5BA4E633", // 20%
  ];

  const buildAllocationRows = (items, variant) => {
    const list = Array.isArray(items) ? items : [];
    const normalizePercent = (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return 0;
      }
      if (numeric > 0 && numeric <= 1) {
        return numeric * 100;
      }
      return numeric;
    };

    const withAllocation = list.map((item) => {
      const allocation =
        variant === "simulated"
          ? normalizePercent(
              Number(item.allocation_percentage) ||
                Number(item.target_allocation_percent) ||
                Number(item.portfolio_allocation) * 100 ||
                0
            )
          : Number(item.portfolio_allocation) * 100 || 0;
      return {
        ...item,
        label: resolveDisplaySymbol(item.symbol),
        allocation,
      };
    });

    if (variant === "simulated") {
      const sorted = [...withAllocation].sort(
        (a, b) => b.allocation - a.allocation
      );
      const colorMap = new Map();
      sorted.forEach((item, index) => {
        const color =
          SIMULATED_COLORS[index] ||
          SIMULATED_COLORS[SIMULATED_COLORS.length - 1];
        colorMap.set(item, color);
      });
      return withAllocation.map((item, index) => ({
        ...item,
        color: colorMap.get(item),
      }));
    }

    return withAllocation.map((item, index) => ({
      ...item,
      color: ASSET_PALETTE[index % ASSET_PALETTE.length],
    }));
  };

  const currentAllocations = useMemo(
    () => buildAllocationRows(assetPool.current, "current"),
    [assetPool.current]
  );

  const simulatedAllocations = useMemo(
    () => buildAllocationRows(assetPool.simulated, "simulated"),
    [assetPool.simulated]
  );

  const assetMonitorRows = useMemo(() => {
    const rows = Array.isArray(assetPool.monitorAll)
      ? assetPool.monitorAll
      : [];
    return rows
      .map((item) => {
        const allocation =
          Number(item.allocation_percentage) ||
          Number(item.target_allocation_percent) ||
          Number(item.portfolio_allocation) * 100 ||
          0;
        const displaySymbol = resolveDisplaySymbol(item.symbol);
        const market =
          marketData.map?.[String(displaySymbol || "").toUpperCase()] || null;
        return {
          ...item,
          displaySymbol,
          allocation,
          market,
        };
      })
      .sort((a, b) => b.allocation - a.allocation);
  }, [assetPool.monitorAll, marketData.map]);

  const assetMonitorUpdatedAt = useMemo(() => {
    const rows = Array.isArray(assetPool.monitorAll)
      ? assetPool.monitorAll
      : [];
    let latest = null;
    rows.forEach((item) => {
      const timestamp = item?.time || item?.utc_timestamp;
      if (!timestamp) return;
      const ts = new Date(timestamp);
      if (Number.isNaN(ts.getTime())) return;
      if (!latest || ts > latest) {
        latest = timestamp;
      }
    });
    if (!latest) return "—";
    const parsed = new Date(latest);
    if (Number.isNaN(parsed.getTime())) {
      return latest;
    }
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [assetPool.monitorAll]);

  const totalAssetPoolCount = useMemo(() => {
    const symbols = new Set();
    (Array.isArray(assetPool.monitorAll) ? assetPool.monitorAll : []).forEach(
      (item) => {
        const label = resolveDisplaySymbol(item.symbol);
        if (label && label !== "—") {
          symbols.add(label);
        }
      }
    );
    return symbols.size;
  }, [assetPool.monitorAll]);

  const totalAssetPoolSymbols = useMemo(() => {
    const symbols = new Set();
    (Array.isArray(assetPool.monitorAll) ? assetPool.monitorAll : []).forEach(
      (item) => {
        const label = resolveDisplaySymbol(item.symbol);
        if (label && label !== "—") {
          symbols.add(label);
        }
      }
    );
    return Array.from(symbols).sort((a, b) => a.localeCompare(b));
  }, [assetPool.monitorAll]);
  const combineCurrentPool = (items) => {
    const map = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
      const displaySymbol = resolveDisplaySymbol(item.symbol);
      const key = displaySymbol.toUpperCase();
      const existing = map.get(key);
      const amount = Number(item.amount) || 0;
      const assetsValue = Number(item.assets_value) || 0;
      const allocation = Number(item.portfolio_allocation) || 0;
      if (!existing) {
        map.set(key, {
          ...item,
          symbol: displaySymbol,
          amount,
          assets_value: assetsValue,
          portfolio_allocation: allocation,
        });
        return;
      }
      existing.amount += amount;
      existing.assets_value += assetsValue;
      existing.portfolio_allocation += allocation;
      if (item.utc_timestamp && !existing.utc_timestamp) {
        existing.utc_timestamp = item.utc_timestamp;
      }
    });
    return Array.from(map.values());
  };

  const getValueClass = (value) => {
    const numeric = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return "";
    }
    return numeric >= 0 ? styles.positive : styles.negative;
  };

  if (loading) {
    return (
      <div className={styles.analytics}>
        <main className={styles.mainContent}>
          <div className={styles.loading}>Loading analytics data...</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.analytics}>
        <main className={styles.mainContent}>
          <div className={styles.error}>
            Error loading analytics data. Please try again later.
          </div>
        </main>
      </div>
    );
  }

  const toNumberOrNull = (value) => {
    const numeric = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const handleExportReport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportError("");

    try {
      if (marketData.loading || revenueData.loading || assetPool.loading) {
        setExportError("Export is waiting for data to finish loading.");
        return;
      }

      const workbook = XLSX.utils.book_new();

      const addSheet = (name, headers, rows) => {
        const data = [headers, ...rows];
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, name);
      };

      addSheet(
        "APR Historical",
        ["Date", "Expected APR", "Implied APR", "90-Day APY", "30-Day APY"],
        historicalRows.map((item) => [
          item.date,
          toNumberOrNull(item.expectedApr) != null
            ? Number(item.expectedApr) * 100
            : null,
          toNumberOrNull(item.impliedApr) != null
            ? Number(item.impliedApr) * 100
            : null,
          toNumberOrNull(item.apy90Day) != null
            ? Number(item.apy90Day) * 100
            : null,
          toNumberOrNull(item.apy30Day) != null
            ? Number(item.apy30Day) * 100
            : null,
        ])
      );

      addSheet(
        "Growth Breakdown",
        ["Month", "Cumulative Growth", "Monthly Change", "Monthly Change %"],
        growthMonthlyBreakdown.map((row) => [
          row.label,
          toNumberOrNull(row.cumulative),
          toNumberOrNull(row.monthlyChange),
          toNumberOrNull(row.monthlyChangePct),
        ])
      );

      addSheet(
        "Assets & USDI",
        ["Month", "Total Assets", "USDI in Circulation", "Difference"],
        assetsMonthlyBreakdown.map((row) => [
          row.label,
          toNumberOrNull(row.totalAssets),
          toNumberOrNull(row.usdi),
          toNumberOrNull(row.difference),
        ])
      );

      addSheet(
        "Asset Monitor",
        [
          "Rank",
          "Symbol",
          "Market Cap",
          "24H Volume",
          "FDV",
          "Allocation %",
          "APR Score",
          "Expected APY",
          "3-Day Funding",
          "7-Day Funding",
          "30-Day Funding",
        ],
        assetMonitorRows.map((row, index) => [
          index + 1,
          row.displaySymbol,
          toNumberOrNull(row.market?.market_cap),
          toNumberOrNull(row.market?.total_volume),
          toNumberOrNull(row.market?.fully_diluted_valuation),
          toNumberOrNull(row.allocation),
          toNumberOrNull(row.apr_score) != null
            ? Number(row.apr_score) * 100
            : null,
          toNumberOrNull(row.expected_apy) != null
            ? Number(row.expected_apy) * 100
            : null,
          toNumberOrNull(row["3_day_cum_funding"]) != null
            ? Number(row["3_day_cum_funding"]) * 100
            : null,
          toNumberOrNull(row["7_day_cum_funding"]) != null
            ? Number(row["7_day_cum_funding"]) * 100
            : null,
          toNumberOrNull(row["30_day_cum_funding"]) != null
            ? Number(row["30_day_cum_funding"]) * 100
            : null,
        ])
      );

      addSheet(
        "Revenue Monthly",
        [
          "Month",
          "Gross Profit",
          "Net Profit",
          "Commission",
          "Cumulative Gross Profit",
          "Cumulative Net Profit",
          "Cumulative Commission",
        ],
        [...revenueData.monthlyBreakdown].slice().reverse().map((row) => [
          formatRevenueMonthLabel(row.month, "long"),
          toNumberOrNull(row.grossProfitValue),
          toNumberOrNull(row.netProfitValue),
          toNumberOrNull(row.commissionValue),
          toNumberOrNull(row.cumulativeGrossProfit),
          toNumberOrNull(row.cumulativeNetProfit),
          toNumberOrNull(row.cumulativeCommission),
        ])
      );

      const timestamp = new Date().toISOString().replace(/[:]/g, "-");
      XLSX.writeFile(
        workbook,
        `cutwater-analytics-report-${timestamp}.xlsx`
      );
    } catch (err) {
      console.error("Export report failed:", err);
      setExportError("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.analytics}>
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Analytics</h1>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.btnSecondary}
              onClick={handleExportReport}
              disabled={isExporting}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: "8px" }}
              >
                <path
                  d="M8 2V10M8 10L5 7M8 10L11 7M2 12H14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isExporting ? "Preparing…" : "Export Report"}
            </button>
            <button
              className={styles.btnSecondary}
              onClick={async () => {
                try {
                  setLoading(true);
                  setError(null);
                  
                  const response = await fetch(
                    `${ENDPOINT_BASE_URL}/track_records_daily_latest`
                  );
                  
                  if (!response.ok) {
                    throw new Error("Failed to fetch analytics data");
                  }
                  
                  const apiData = await response.json();
                  const records = Array.isArray(apiData?.data) ? apiData.data : [];
                  
                  const transformedData = transformRecords(records);
                  const sortedData = transformedData.sort((a, b) => {
                    const dateA = toUtcDate(a.date);
                    const dateB = toUtcDate(b.date);
                    if (!dateA || !dateB) return 0;
                    return dateB.getTime() - dateA.getTime();
                  });
                  
                  setData(sortedData);

                  const [currentRaw, simulatedRaw] = await Promise.all([
                    fetchPortfolioLatestData(),
                    fetchMonitorAprAttributes(),
                  ]);

                  const currentFiltered = combineCurrentPool(
                    (Array.isArray(currentRaw) ? currentRaw : []).filter(
                      (item) => Number(item.amount) > 0
                    )
                  );
                  const totalValue = currentFiltered.reduce(
                    (sum, item) => sum + (Number(item.assets_value) || 0),
                    0
                  );
                  const simulatedAll = Array.isArray(simulatedRaw) ? simulatedRaw : [];
                  const simulatedFiltered = simulatedAll.filter(
                    (item) => {
                      const target =
                        Number(item.allocation_percentage) ||
                        Number(item.target_allocation_percent) ||
                        Number(item.portfolio_allocation) * 100 ||
                        0;
                      return target > 0;
                    }
                  );

                  setAssetPool({
                    current: currentFiltered,
                    simulated: simulatedFiltered,
                    monitorAll: simulatedAll,
                    totalValue,
                    loading: false,
                  });

                  const debugMap = buildMonitorAprMap(simulatedFiltered);
                  console.log("Asset pool APR mapping (debug):", {
                    simulatedSample: simulatedFiltered.slice(0, 5).map((item) => ({
                      symbol: item.symbol,
                      utc_timestamp: item.utc_timestamp,
                      apr_score: item.apr_score,
                    })),
                    simulatedCount: simulatedFiltered.length,
                    currentCount: currentFiltered.length,
                    samples: currentFiltered.map((item) => {
                      const monitorSymbol = resolveMonitorSymbol(item.symbol);
                      const monitorRow = debugMap.get(monitorSymbol);
                      return {
                        symbol: item.symbol,
                        displaySymbol: resolveDisplaySymbol(item.symbol),
                        monitorSymbol,
                        apr_score: monitorRow?.apr_score ?? null,
                      };
                    }),
                  });
                } catch (err) {
                  console.error("Error refreshing analytics data:", err);
                  setError(err);
                } finally {
                  setLoading(false);
                }
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: "8px" }}
              >
                <path
                  d="M13.5 7.75a5.75 5.75 0 1 1-1.35-3.7M13.5 3.5v4h-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Refresh Data
            </button>
          </div>
        </div>

        {exportError ? (
          <div className={styles.error} role="alert">
            {exportError}
          </div>
        ) : null}

        <div className={styles.categoryTabs}>
          {categories.map((category) => (
            <button
              key={category}
              className={`${styles.categoryTab} ${
                selectedCategory === category ? styles.active : ""
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {isMobile
                ? {
                    "APR Metrics": "APR",
                    "Growth Metrics": "Growth",
                    "Asset Pool Metrics": "Asset",
                    "Revenue & Commission": "Revenue",
                  }[category] || category
                : category}
            </button>
          ))}
        </div>

        {selectedCategory === "APR Metrics" && (
          <>
            <div className={styles.timeFiltersAndLegend}>
              <div className={styles.timeFilters}>
                {timeRanges.map((range) => (
                  <button
                    key={range}
                    className={`${styles.timeFilterBtn} ${
                      timeRange === range ? styles.active : ""
                    }`}
                    onClick={() => setTimeRange(range)}
                  >
                    {range}
                  </button>
                ))}
              </div>

              <div className={styles.chartLegend}>
                <div className={styles.legendItem}>
                  <span
                    className={styles.legendDot}
                    style={{ backgroundColor: "#5BA4E6" }}
                  ></span>
                  <span>Expected APR</span>
                </div>
                <div className={styles.legendItem}>
                  <span
                    className={styles.legendDot}
                    style={{ backgroundColor: "#1a2b5f" }}
                  ></span>
                  <span>Implied APR</span>
                </div>
                <div className={styles.legendItem}>
                  <span
                    className={styles.legendDot}
                    style={{ backgroundColor: "#2d8a5f" }}
                  ></span>
                  <span>90-Day APY</span>
                </div>
                <div className={styles.legendItem}>
                  <span
                    className={styles.legendDot}
                    style={{ backgroundColor: "#c9a227" }}
                  ></span>
                  <span>30-Day APY</span>
                </div>
              </div>
            </div>

            <div className={styles.chartContainer}>
              <AnalyticsCharts
                chartData={chartData}
                timeRange={timeRange}
              />
            </div>

            <div className={styles.summaryCards}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Current Expected APR</div>
                <div
                  className={`${styles.summaryValue} ${getValueClass(
                    currentValues.expectedApr * 100
                  )}`}
                >
                  {formatPercent(currentValues.expectedApr * 100)}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Current Implied APR</div>
                <div
                  className={`${styles.summaryValue} ${getValueClass(
                    currentValues.impliedApr * 100
                  )}`}
                >
                  {formatPercent(currentValues.impliedApr * 100)}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>90-Day APY</div>
                <div
                  className={`${styles.summaryValue} ${
                    currentValues.apy90Day != null
                      ? getValueClass(currentValues.apy90Day * 100)
                      : ""
                  }`}
                >
                  {currentValues.apy90Day != null
                    ? formatPercent(currentValues.apy90Day * 100)
                    : "—"}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>30-Day APY</div>
                <div
                  className={`${styles.summaryValue} ${
                    currentValues.apy30Day != null
                      ? getValueClass(currentValues.apy30Day * 100)
                      : ""
                  }`}
                >
                  {currentValues.apy30Day != null
                    ? formatPercent(currentValues.apy30Day * 100)
                    : "—"}
                </div>
              </div>
            </div>

            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>Historical Data</h2>
                <span className={styles.tableCount}>
                  {filteredData.length} records
                </span>
              </div>
              <div
                className={`${styles.tableWrapper} ${styles.tableWrapperScrollable}`}
              >
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Expected APR</th>
                      <th>Implied APR</th>
                      <th>90-Day APY</th>
                      <th>30-Day APY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicalRows.map((item, index) => (
                      <tr key={`${item.date}-${index}`}>
                        <td>{item.date}</td>
                        <td
                          className={`${getValueClass(item.expectedApr * 100)}`}
                        >
                          {formatPercent(item.expectedApr * 100)}
                        </td>
                        <td
                          className={`${getValueClass(item.impliedApr * 100)}`}
                        >
                          {formatPercent(item.impliedApr * 100)}
                        </td>
                        <td
                          className={`${
                            item.apy90Day != null
                              ? getValueClass(item.apy90Day * 100)
                              : ""
                          }`}
                        >
                          {item.apy90Day != null
                            ? formatPercent(item.apy90Day * 100)
                            : "—"}
                        </td>
                        <td
                          className={`${
                            item.apy30Day != null
                              ? getValueClass(item.apy30Day * 100)
                              : ""
                          }`}
                        >
                          {item.apy30Day != null
                            ? formatPercent(item.apy30Day * 100)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {selectedCategory === "Growth Metrics" && (
          <>
            <div className={`${styles.summaryCards} ${styles.summaryCardsGrowth}`}>
              <div className={`${styles.summaryCard} ${styles.summaryCardValueTop}`}>
                <div className={styles.summaryLabel}>Total Growth</div>
                <div
                  className={`${styles.summaryValue} ${getValueClass(
                    growthSummary.total
                  )}`}
                >
                  {formatPercent(growthSummary.total)}
                </div>
              </div>
              {yearlyGrowthCards.map((item) => (
                <div
                  className={`${styles.summaryCard} ${styles.summaryCardValueTop}`}
                  key={item.year}
                >
                  <div className={styles.summaryLabel}>{item.year} Growth</div>
                  <div
                    className={`${styles.summaryValue} ${getValueClass(
                      item.value
                    )}`}
                  >
                    {formatPercent(item.value)}
                  </div>
                </div>
              ))}
            </div>

            <AnalyticsGrowthCharts
              growthSeries={growthSeries}
              monthlyGrowth={monthlyGrowth}
              assetsSeries={assetsSeries}
            />

            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>Growth Breakdown</h2>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Cumulative Growth</th>
                      <th>Monthly Change</th>
                      <th>Monthly Change %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {growthMonthlyBreakdown.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td
                          className={
                            row.cumulative != null && Number.isFinite(row.cumulative)
                              ? row.cumulative >= 1
                                ? styles.positive
                                : styles.negative
                              : ""
                          }
                        >
                          {formatGrowthMultiple(row.cumulative)}
                        </td>
                        <td className={getValueClass(row.monthlyChange)}>
                          {row.monthlyChange != null
                            ? `${row.monthlyChange >= 0 ? "+" : ""}${row.monthlyChange.toFixed(6)}`
                            : "—"}
                        </td>
                        <td className={getValueClass(row.monthlyChangePct)}>
                          {row.monthlyChangePct != null &&
                          Number.isFinite(row.monthlyChangePct)
                            ? `${row.monthlyChangePct >= 0 ? "+" : "-"}${Math.abs(
                                row.monthlyChangePct
                              ).toFixed(2)}%`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>Assets & USDI Monthly Breakdown</h2>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Total Assets</th>
                      <th>USDI in Circulation</th>
                      <th>Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetsMonthlyBreakdown.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td>{formatCurrency(row.totalAssets)}</td>
                        <td>{formatCurrency(row.usdi)}</td>
                        <td className={getValueClass(row.difference)}>
                          {formatCurrency(row.difference)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {selectedCategory !== "APR Metrics" &&
          selectedCategory !== "Growth Metrics" &&
          selectedCategory !== "Asset Pool Metrics" &&
          selectedCategory !== "Revenue & Commission" && (
          <div className={styles.comingSoon}>
            <p>Coming soon: {selectedCategory} data</p>
          </div>
        )}

        {selectedCategory === "Asset Pool Metrics" && (
          <div className={styles.assetPoolSection}>
            <p className={styles.assetPoolSubtitle}>
              Perpetual futures assets in the Cutwater trading pool
            </p>
            <div className={styles.assetPoolGrid}>
              <div className={styles.assetPoolCard}>
                <div className={styles.assetPoolHeader}>
                  <h2 className={styles.tableTitle}>Current Asset Pool</h2>
                </div>
                <div className={styles.assetPoolBody}>
                  <div className={styles.assetPoolRingBlock}>
                    <div className={styles.assetPoolTotal}>
                      <span className={styles.assetPoolTotalLabel}>Total:</span>
                      <span className={styles.assetPoolTotalValue}>
                        {formatCurrencyWhole(assetPool.totalValue)}
                      </span>
                    </div>
                    <AnalyticsAssetPoolCharts
                      allocation={currentAllocations.map((item) => ({
                        label: item.label,
                        value: item.allocation,
                        color: item.color,
                      }))}
                      variant="current"
                    />
                  </div>
                  <div className={styles.assetPoolList}>
                    {currentAllocations.map((item) => (
                      <div className={styles.assetPoolRow} key={`${item.symbol}-${item.utc_timestamp}`}>
                        <div className={styles.assetPoolMeta}>
                          <span
                            className={styles.assetPoolBadge}
                            style={{ background: item.color }}
                          >
                            {item.label?.[0] || "—"}
                          </span>
                          <div className={styles.assetPoolText}>
                            <span className={styles.assetPoolSymbol}>
                              {item.label}
                            </span>
                            <span className={styles.assetPoolApr}>
                              {formatAprLabel(
                                item.label,
                                Number(
                                  monitorAprMap.get(
                                    resolveMonitorSymbol(item.symbol)
                                  )?.apr_score
                                ) * 100
                              )}
                            </span>
                          </div>
                        </div>
                        <div className={styles.assetPoolValues}>
                          <div className={styles.assetPoolValue}>
                            {formatCurrencyShort(item.assets_value)}
                          </div>
                          <div className={styles.assetPoolPercent}>
                            {formatPercent(item.allocation)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.assetPoolCard}>
                <div className={styles.assetPoolHeader}>
                  <h2 className={styles.tableTitle}>Simulated Asset Pool</h2>
                </div>
                <div className={styles.assetPoolBody}>
                  <div className={styles.assetPoolRingBlock}>
                    <div className={styles.assetPoolRingTitle}>
                      Optimized Allocation
                    </div>
                    <AnalyticsAssetPoolCharts
                      allocation={simulatedAllocations.map((item) => ({
                        label: resolveDisplaySymbol(item.symbol),
                        value: item.allocation,
                        color: item.color,
                      }))}
                      variant="simulated"
                    />
                  </div>
                  <div className={styles.assetPoolList}>
                    {simulatedAllocations.map((item) => (
                      <div className={styles.assetPoolRow} key={`${item.symbol}-${item.utc_timestamp}`}>
                        <div className={styles.assetPoolMeta}>
                          <span
                            className={styles.assetPoolBadge}
                            style={{ background: item.color }}
                          >
                            {resolveDisplaySymbol(item.symbol)?.[0] || "—"}
                          </span>
                          <div className={styles.assetPoolText}>
                            <span className={styles.assetPoolSymbol}>
                              {resolveDisplaySymbol(item.symbol)}
                            </span>
                            <span className={styles.assetPoolApr}>
                              {formatBasisPercent(Number(item.expected_apy) * 100)} APR
                            </span>
                          </div>
                        </div>
                        <div className={styles.assetPoolValues}>
                          <div className={styles.assetPoolValue}>
                            {formatPercent(item.allocation)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.assetPoolFooterCard}>
              <div className={styles.assetPoolFooterTitle}>
                Total Asset Pool ({totalAssetPoolCount} assets)
              </div>
              <div className={styles.assetPoolChips}>
                {totalAssetPoolSymbols.map((symbol) => (
                  <span className={styles.assetPoolChip} key={symbol}>
                    {symbol}
                  </span>
                ))}
              </div>
            </div>
            <div className={styles.tableCard}>
              <div className={styles.assetMonitorHeader}>
                <h2 className={styles.tableTitle}>Asset Monitor</h2>
                <span className={styles.assetMonitorUpdated}>
                  Last updated: {assetMonitorUpdatedAt}
                </span>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Symbol</th>
                      <th>Market Cap</th>
                      <th>24H Volume</th>
                      <th>FDV</th>
                      <th>Allocation %</th>
                      <th>APR Score</th>
                      <th>Expected APY</th>
                      <th>3-Day Funding</th>
                      <th>7-Day Funding</th>
                      <th>30-Day Funding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetMonitorRows.map((row, index) => (
                      <tr key={`${row.symbol}-${row.utc_timestamp}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{row.displaySymbol}</td>
                        <td>{formatCompactCurrency(row.market?.market_cap)}</td>
                        <td>{formatCompactCurrency(row.market?.total_volume)}</td>
                        <td>
                          {formatCompactCurrency(
                            row.market?.fully_diluted_valuation
                          )}
                        </td>
                        <td className={getValueClass(row.allocation)}>
                          {formatPercent(row.allocation)}
                        </td>
                        <td className={getValueClassFromDecimal(row.apr_score)}>
                          {formatPercentFromDecimal(row.apr_score)}
                        </td>
                        <td
                          className={getValueClassFromDecimal(row.expected_apy)}
                        >
                          {formatPercentFromDecimal(row.expected_apy)}
                        </td>
                        <td
                          className={getValueClassFromDecimal(
                            row["3_day_cum_funding"]
                          )}
                        >
                          {formatPercentFromDecimal(row["3_day_cum_funding"])}
                        </td>
                        <td
                          className={getValueClassFromDecimal(
                            row["7_day_cum_funding"]
                          )}
                        >
                          {formatPercentFromDecimal(row["7_day_cum_funding"])}
                        </td>
                        <td
                          className={getValueClassFromDecimal(
                            row["30_day_cum_funding"]
                          )}
                        >
                          {formatPercentFromDecimal(row["30_day_cum_funding"])}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {selectedCategory === "Revenue & Commission" && (
          <div className={styles.revenueSection}>
            <p className={styles.assetPoolSubtitle}>
              Monthly and cumulative growth metrics for Cutwater Partners
              {revenueDateRangeLabel ? ` (${revenueDateRangeLabel})` : ""}
            </p>
            <div className={styles.revenueHeader}>
              <div className={styles.revenueToggle}>
                {["Monthly", "Cumulative"].map((range) => (
                  <button
                    key={range}
                    className={`${styles.revenueTab} ${
                      revenueRange === range ? styles.revenueTabActive : ""
                    }`}
                    onClick={() => setRevenueRange(range)}
                  >
                    {range}
                  </button>
                ))}
              </div>
                <div className={styles.revenueLegend}>
                  <div className={styles.legendItem}>
                    <span
                      className={styles.revenueLegendDot}
                      style={{ backgroundColor: "#2d8a5f" }}
                    ></span>
                    <span>Net Profit</span>
                  </div>
                  <div className={styles.legendItem}>
                    <span
                      className={styles.revenueLegendDot}
                      style={{ backgroundColor: "#5BA4E6" }}
                    ></span>
                    <span>Commission</span>
                  </div>
                  <div className={styles.legendItem}>
                    <span
                      className={styles.revenueLegendDot}
                      style={{ backgroundColor: "#1a2b5f" }}
                    ></span>
                    <span>Gross Profit</span>
                  </div>
              </div>
            </div>
            <div className={styles.summaryCards}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Gross Profit</div>
                <div className={styles.summaryValue}>
                  {formatCurrencyWhole(revenueData.totals.grossProfit)}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Net Profit</div>
                <div className={styles.summaryValue}>
                  {formatCurrencyWhole(revenueData.totals.netProfit)}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Total Commission</div>
                <div className={styles.summaryValue}>
                  {formatCurrencyWhole(revenueData.totals.commission)}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Commission Rate</div>
                <div className={styles.summaryValue}>
                  {revenueData.totals.rate != null
                    ? formatPercent(revenueData.totals.rate)
                    : "—"}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Peak Monthly Net Profit</div>
                <div className={styles.summaryValue}>
                  {formatCurrencyWhole(revenueData.totals.peakNetProfit)}
                </div>
              </div>
            </div>
            <div className={styles.revenueChartCard}>
              <AnalyticsRevenueCharts
                revenueSeries={revenueData.revenueSeries.map((item) => ({
                  ...item,
                  month: shiftRevenueMonthKey(item.month),
                }))}
                commissionSeries={revenueData.commissionSeries.map((item) => ({
                  ...item,
                  month: shiftRevenueMonthKey(item.month),
                }))}
                range={revenueRange}
              />
            </div>
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>Monthly Breakdown</h2>
                <span className={styles.tableCount}>
                  {revenueData.monthlyBreakdown.length} records
                </span>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Gross Profit</th>
                      <th>Net Profit</th>
                      <th>Commission</th>
                      <th>Cumulative Gross Profit</th>
                      <th>Cumulative Net Profit</th>
                      <th>Cumulative Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...revenueData.monthlyBreakdown]
                      .slice()
                      .reverse()
                      .map((row) => (
                        <tr key={row.month}>
                          <td>
                            {formatRevenueMonthLabel(row.month, "long")}
                          </td>
                          <td>{formatCurrencyOrDash(row.grossProfitValue)}</td>
                          <td>{formatCurrencyOrDash(row.netProfitValue)}</td>
                          <td>{formatCurrency(row.commissionValue)}</td>
                          <td>{formatCurrencyOrDash(row.cumulativeGrossProfit)}</td>
                          <td>{formatCurrencyOrDash(row.cumulativeNetProfit)}</td>
                          <td>{formatCurrencyOrDash(row.cumulativeCommission)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className={styles.analyticsFooter}>
          <span className={styles.footerText}>
            &copy; 2025 Cutwater Partners. All rights reserved.
          </span>
          <div className={styles.footerLinks}>
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a href="#">Risk Disclosure</a>
          </div>
        </div>
      </main>
    </div>
  );
}
